import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

const SystemStatusIndicator = () => {
  const [systemStatus, setSystemStatus] = useState({
    ais: { status: 'connected', lastUpdate: '2 min ago' },
    vms: { status: 'connected', lastUpdate: '1 min ago' },
    iot: { status: 'warning', lastUpdate: '5 min ago' },
    trading: { status: 'connected', lastUpdate: '30 sec ago' }
  });
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate real-time status updates
      setSystemStatus(prev => ({
        ...prev,
        trading: { 
          ...prev?.trading, 
          lastUpdate: Math.floor(Math.random() * 60) + ' sec ago' 
        }
      }));
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'text-success';
      case 'warning': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return 'CheckCircle';
      case 'warning': return 'AlertTriangle';
      case 'error': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'connected': return 'bg-success/10';
      case 'warning': return 'bg-warning/10';
      case 'error': return 'bg-error/10';
      default: return 'bg-muted';
    }
  };

  const systemItems = [
    { key: 'ais', label: 'AIS Tracking', icon: 'Radar' },
    { key: 'vms', label: 'VMS Data', icon: 'Satellite' },
    { key: 'iot', label: 'IoT Sensors', icon: 'Cpu' },
    { key: 'trading', label: 'Trading Feed', icon: 'TrendingUp' }
  ];

  const hasWarnings = Object.values(systemStatus)?.some(s => s?.status !== 'connected');

  return (
    <div className="relative">
      {/* Status Indicator Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-xs font-medium transition-maritime ${
          hasWarnings 
            ? 'bg-warning/10 text-warning hover:bg-warning/20' :'bg-success/10 text-success hover:bg-success/20'
        }`}
      >
        <Icon 
          name={hasWarnings ? 'AlertTriangle' : 'CheckCircle'} 
          size={14} 
          strokeWidth={2} 
        />
        <span className="hidden sm:inline">
          {hasWarnings ? 'System Alert' : 'All Systems'}
        </span>
        <Icon 
          name="ChevronDown" 
          size={12} 
          strokeWidth={2}
          className={`transition-transform duration-200 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>
      {/* Expanded Status Panel */}
      {isExpanded && (
        <>
          <div 
            className="fixed inset-0 z-1000" 
            onClick={() => setIsExpanded(false)}
          />
          <div className="absolute right-0 top-full mt-2 w-72 bg-popover border border-border rounded-lg shadow-maritime-lg z-1100 animate-fade-in">
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground">System Status</h3>
                <button
                  onClick={() => setIsExpanded(false)}
                  className="p-1 hover:bg-muted rounded"
                >
                  <Icon name="X" size={14} strokeWidth={2} />
                </button>
              </div>
              
              <div className="space-y-3">
                {systemItems?.map((item) => {
                  const status = systemStatus?.[item?.key];
                  return (
                    <div 
                      key={item?.key}
                      className={`flex items-center justify-between p-3 rounded-md ${getStatusBg(status?.status)}`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon name={item?.icon} size={16} strokeWidth={2} />
                        <div>
                          <div className="text-sm font-medium text-foreground">
                            {item?.label}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Updated {status?.lastUpdate}
                          </div>
                        </div>
                      </div>
                      <Icon 
                        name={getStatusIcon(status?.status)} 
                        size={16} 
                        strokeWidth={2}
                        className={getStatusColor(status?.status)}
                      />
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-4 pt-3 border-t border-border">
                <button className="w-full text-xs text-muted-foreground hover:text-foreground transition-maritime">
                  View Detailed System Logs
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SystemStatusIndicator;